import React, { useEffect, useMemo, useState } from "react";
import { FaChalkboardTeacher, FaRegListAlt, FaUsers } from "react-icons/fa";
import { GiDiploma } from "react-icons/gi";

const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

const useCountUp = (target = 0, duration = 750) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let rafId;
    let startTs;

    const finalValue = Number(target || 0);
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (timestamp) => {
      if (!startTs) startTs = timestamp;

      const progress = Math.min(1, (timestamp - startTs) / duration);
      const eased = easeOutCubic(progress);

      setValue(Math.round(finalValue * eased));

      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    setValue(0);
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [target, duration]);

  return value;
};

const useAnimatedPercent = (target = 0, duration = 850) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let rafId;
    let startTs;

    const finalValue = Math.max(0, Math.min(100, Number(target || 0)));
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (timestamp) => {
      if (!startTs) startTs = timestamp;

      const progress = Math.min(1, (timestamp - startTs) / duration);
      const eased = easeOutCubic(progress);

      setValue(finalValue * eased);

      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    setValue(0);
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [target, duration]);

  return value;
};

const StatCard = ({ icon: Icon, label, value, subtitle, loading = false }) => {
  const animatedValue = useCountUp(loading ? 0 : value);

  if (loading) {
    return (
      <div className="rounded-[1.25rem] border border-slate-200 bg-white/90 p-3 shadow-sm dark:border-white/10 dark:bg-slate-900/75 sm:p-4">
        <div className="mb-3 h-8 w-8 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
        <div className="mb-2 h-3 w-20 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="mb-2 h-7 w-14 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-3 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
    );
  }

  return (
    <div className="rounded-[1.25rem] border border-slate-200 bg-white/90 p-3 shadow-sm backdrop-blur transition duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/75 sm:p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20 sm:h-9 sm:w-9">
          <Icon className="text-sm sm:text-base" />
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-semibold leading-tight text-slate-500 dark:text-slate-400 sm:text-xs">
            {label}
          </p>

          <p className="mt-1 text-2xl font-extrabold leading-none text-slate-950 dark:text-white">
            {formatNumber(animatedValue)}
          </p>

          {subtitle && (
            <p className="mt-2 text-[10px] leading-4 text-slate-500 dark:text-slate-400 sm:text-[11px]">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const StatisticsSection = ({
  title = "Quelques chiffres",
  visualUrl = "/img/headercount_new.webp",
  kanjiUrl = "/img/judo-kanji.webp",
  loading = false,
  licencies = 0,
  groupes = 0,
  enseignants = 0,
  inscritsEtCoursEssai = 0,
}) => {
  const licensedCount = Number(licencies || 0);
  const prospectCount = Number(inscritsEtCoursEssai || 0);
  const totalPeople = licensedCount + prospectCount;

  const percentLicensed = useMemo(() => {
    if (totalPeople <= 0) return 0;
    return Math.round((licensedCount / totalPeople) * 100);
  }, [licensedCount, totalPeople]);

  const animatedPercent = useAnimatedPercent(
    loading ? 0 : percentLicensed,
    850,
  );

  return (
    <section className="relative overflow-hidden bg-slate-50 py-10 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-14">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.10),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_34%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
          <div className="grid lg:grid-cols-[0.72fr_1.28fr]">
            <div className="relative h-[230px] overflow-hidden bg-slate-950 sm:h-[280px] lg:h-auto lg:min-h-[390px]">
              <img
                src={visualUrl}
                alt="Judokas en action"
                loading="lazy"
                className="block h-full w-full object-cover object-left lg:hidden"
              />

              <img
                src={kanjiUrl || visualUrl}
                alt="Idéogrammes Judo"
                loading="lazy"
                className="hidden h-full w-full object-cover object-center lg:block"
              />

              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-950/35 to-slate-950/20" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent" />

              <div className="absolute left-5 right-5 top-5 sm:left-7 sm:right-7 sm:top-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 backdrop-blur-xl">
                  Statistiques du club
                </div>

                <h3 className="mt-4 max-w-sm font-barlow text-2xl font-extrabold leading-tight text-white drop-shadow-lg sm:text-3xl">
                  Une vision claire de la dynamique du club
                </h3>

                <p className="mt-3 max-w-md text-sm leading-6 text-white/75">
                  Effectif licencié, essais, groupes et encadrement : les
                  chiffres essentiels du club, mis à jour automatiquement.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-[#f7f4f2] via-white to-[#fff8f2] p-5 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 sm:p-6 lg:p-8">
              <div className="mb-5">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                  Données dynamiques
                </div>

                <h2 className="mt-4 font-barlow text-3xl font-extrabold uppercase tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                  {title}
                </h2>

                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Les indicateurs ci-dessous sont calculés à partir des données
                  du club.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <StatCard
                  icon={FaUsers}
                  label="Licenciés"
                  value={licensedCount}
                  subtitle="sur la saison en cours"
                  loading={loading}
                />

                <StatCard
                  icon={FaRegListAlt}
                  label="Inscrits & essais"
                  value={prospectCount}
                  subtitle="sur la saison en cours"
                  loading={loading}
                />

                <StatCard
                  icon={GiDiploma}
                  label="Groupes"
                  value={groupes}
                  subtitle="groupes actifs"
                  loading={loading}
                />

                <StatCard
                  icon={FaChalkboardTeacher}
                  label="Enseignants"
                  value={enseignants}
                  subtitle="diplômés & en formation"
                  loading={loading}
                />
              </div>

              <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Jauge dynamique
                    </p>

                    <h3 className="mt-1 text-base font-extrabold text-slate-950 dark:text-white">
                      Licenciés / Inscrits et Cours d'essai
                    </h3>
                  </div>

                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-950 dark:text-white">
                      {loading
                        ? "…"
                        : `${formatNumber(licensedCount)} / ${formatNumber(
                            totalPeople,
                          )}`}
                    </p>

                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      part des licenciés
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    <span>0%</span>
                    <span>100%</span>
                  </div>

                  <div
                    className="h-3 overflow-hidden rounded-full border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-slate-800"
                    role="progressbar"
                    aria-valuenow={percentLicensed}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Taux de licenciés"
                  >
                    <div
                      className="h-full rounded-full transition-[width] duration-1000 ease-out"
                      style={{
                        width: loading ? "0%" : `${animatedPercent}%`,
                        background:
                          "linear-gradient(90deg, #15803d 0%, #22c55e 55%, #86efac 100%)",
                        boxShadow: "inset 0 0 8px rgba(255,255,255,0.35)",
                      }}
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                      <span className="inline-block h-3 w-3 rounded-full bg-green-500" />
                      Licenciés
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                      <span className="inline-block h-3 w-3 rounded-full bg-slate-300 dark:bg-slate-600" />
                      Inscrits & essais
                    </div>

                    <div className="text-xs font-bold text-slate-950 dark:text-white">
                      {loading
                        ? "…"
                        : `${Math.round(animatedPercent)}% licenciés`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;
